import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { FullPageSpinner } from "components/Spinner";
import { useSessionJWT, useSessionCtx, usePostMessage } from "utils";
import { getSettings, getAppSettings, logError } from "api/accounts-customer";
import EmptyState from "components/EmptyState";

const useIntegratorSettings = () => {
  const { sessionJWT } = useSessionJWT();
  const fetchIntegratorSettings = useCallback(() => getSettings(sessionJWT), [sessionJWT]);
  const jwtExists = !!sessionJWT;
  return useQuery({
    queryKey: ["settings", "integrator"],
    queryFn: fetchIntegratorSettings,
    enabled: jwtExists
  });
};

const useAppSettings = () => {
  return useQuery({
    queryKey: ["settings", "app"],
    queryFn: getAppSettings
  });
};

const SettingsContext = createContext();

export const SettingsProvider = (props) => {
  const { debug, sessionCtx } = useSessionCtx();
  const { postOnError } = usePostMessage((sessionCtx && sessionCtx.flow) || "", sessionCtx?.intCtx);
  const integratorSettings = useIntegratorSettings();
  const appSettings = useAppSettings();

  debug("SETTINGS: ", appSettings, integratorSettings);

  const value = useMemo(
    () => ({
      appSettings: appSettings?.data || {},
      integratorSettings: integratorSettings?.data || {},
    }),
    [appSettings, integratorSettings]
  );

  if (appSettings.isLoading || integratorSettings.isLoading) {
    return <FullPageSpinner data-testid="appsettings-spinner" />;
  }

  const error = appSettings.error || integratorSettings.error;
  if (error) {
    debug("Settings error", error);
    logError(
      {},
      {
        name: "Settings error",
        error: error.message,
      }
    );
    postOnError({ message: "Settings error" }, error);
    return <EmptyState error={error} />;
  }

  return <SettingsContext.Provider value={value} {...props} />;
};

export const useSettings = () => useContext(SettingsContext);
